import { Ref } from 'react';
import { FormControl, MenuItem, Select, TextField } from '@mui/material';
import { CustomFieldDefinition } from '../../../types';
import { DateValueEditor } from './DateValueEditor';
import {
  BOOLEAN_VALUE_OPTIONS,
  DATA_TYPE_BOOLEAN,
  DATA_TYPE_DATE,
  DATA_TYPE_LIST,
  DataTypeValue,
  isUnreadableStoredDate,
  normaliseFieldType,
  optionsIncluding
} from '../../../utils/customFieldTypes';

export interface CustomFieldValueEditorProps {
  definition: CustomFieldDefinition;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  inputRef: Ref<HTMLInputElement>;
  onKeyDown: (event: React.KeyboardEvent) => void;
  // Reports whether what is on screen is worth saving; only a date can be mid-typed or out of range.
  onValidityChange: (valid: boolean) => void;
  // The row as stored, which for a date the picker cannot hold differs from what the editor opens on.
  storedValue: string;
}

const FONT = { fontSize: '0.875rem' };

const definedChoices = (definition: CustomFieldDefinition, fieldType: DataTypeValue) => {
  if (fieldType === DATA_TYPE_BOOLEAN) return BOOLEAN_VALUE_OPTIONS;
  if (fieldType !== DATA_TYPE_LIST) return [];

  return (definition.options?.choices ?? []).map((choice) => ({ value: choice, label: choice }));
};

const choicesFor = (definition: CustomFieldDefinition, fieldType: DataTypeValue, value: string) => {
  const defined = definedChoices(definition, fieldType);

  return defined.length === 0 ? defined : optionsIncluding(defined, value);
};

export const CustomFieldValueEditor = ({
  definition,
  value,
  onChange,
  disabled,
  inputRef,
  onKeyDown,
  onValidityChange,
  storedValue
}: CustomFieldValueEditorProps) => {
  const fieldType = normaliseFieldType(definition.field_type);

  if (fieldType === DATA_TYPE_DATE) {
    return (
      <DateValueEditor
        value={value}
        disabled={disabled}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onValidityChange={onValidityChange}
        replacing={isUnreadableStoredDate(storedValue) ? storedValue : ''}
      />
    );
  }

  const choices = choicesFor(definition, fieldType, value);
  if (choices.length > 0) {
    return (
      <FormControl
        size="small"
        fullWidth>
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          inputRef={inputRef}
          sx={FONT}>
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {choices.map((choice) => (
            <MenuItem
              key={choice.value}
              value={choice.value}>
              {choice.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <TextField
      size="small"
      fullWidth
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      inputRef={inputRef}
      onKeyDown={onKeyDown}
      inputProps={{ style: FONT }}
    />
  );
};
